import { useFormikContext } from 'formik';
import get from 'lodash/get';
import React from 'react';

import { AutocompleteSelect, DefaultItemProps, Select } from '@candu-ai/analytics/ui';
import { LogicRulesNames, notLogicRule } from '@candu-ai/segments/types';
import { FormikFieldSetter } from 'craft/types';
import { SelectInputWrapper } from 'dashboard/pages/Segment/components/DefinitionFormFields/DefinitionFormComponents';
import { DefinitionInput } from 'dashboard/pages/Segment/components/DefinitionFormFields/DefinitionInput';
import {
  autoCompleteStringOperands,
  defaultOperations,
  isStringDefinedOptions,
  multiselectStringOperands,
} from 'dashboard/pages/Segment/components/constants';
import { DefinitionFormikValues } from 'dashboard/pages/Segment/components/types';

import { SetRuleSummary, UploadButton } from './SetContainsRule';

interface renderRuleOperandFieldProps {
  values: DefinitionFormikValues;
  setFieldValue: FormikFieldSetter;
  base: string;
  operationType: string;
  readOnly?: boolean;
}

const renderRuleOperandField = ({
  base,
  operationType,
  values,
  setFieldValue,
  readOnly,
}: renderRuleOperandFieldProps) => {
  const operationSetPath = `${base}.operation.setId`;
  const operationOperandPath = `${base}.operation.operand`;

  const shouldShowMultiselect = multiselectStringOperands.includes(operationType);

  const shouldShowString =
    operationType && !isStringDefinedOptions.some((option) => option.value === operationType);

  const shouldShowFreeFormInput = !autoCompleteStringOperands.includes(operationType);

  const apiQuery = (search: string) => ({
    query: {
      finder: 'byUserId',
      q: `${search.trim()}`,
    },
  });

  const getFormattedResults = (results: string[]) => {
    return (
      results.map((result) => {
        return { label: result, value: result };
      }) || []
    );
  };

  const setId = get(values, operationSetPath);

  if (!operationType || operationType === '') {
    return <></>;
  }

  if (setId) {
    return <SetRuleSummary base={base} setId={setId} setFieldValue={setFieldValue} />;
  }

  if (shouldShowString && shouldShowFreeFormInput) {
    return <DefinitionInput type="text" name={operationOperandPath} readOnly={readOnly} />;
  }

  return (
    <AutocompleteSelect<DefaultItemProps, string[], DefinitionFormikValues>
      name={operationOperandPath}
      apiQuery={apiQuery}
      readOnly={readOnly}
      getFormattedResults={getFormattedResults}
      isMultiselect={shouldShowMultiselect}
      allowCustomValues
      InputWrapper={SelectInputWrapper}
      data-cy="segment-definition-selector"
      values={values}
      setFieldValue={setFieldValue}
      disableQueryOnEmptySearch
    />
  );
};

export interface DefaultRuleProps {
  base: string;
  ruleType: string;
  readOnly?: boolean;
}

export const DefaultRule = ({ base, ruleType, readOnly }: DefaultRuleProps) => {
  const { values, setFieldValue } = useFormikContext<DefinitionFormikValues>();

  const operationTypePath = `${base}.${ruleType}.operation.type`;

  const operationType = get(values, operationTypePath, null);

  return (
    <>
      <Select<DefaultItemProps, DefinitionFormikValues>
        name={operationTypePath}
        options={defaultOperations}
        readOnly={readOnly}
        placeholder="Choose operator"
        InputWrapper={SelectInputWrapper}
        data-cy="segment-definition-selector"
        values={values}
        setFieldValue={setFieldValue}
        validateOnSelectWithErrors
      />

      {operationType &&
        operationType !== '' &&
        renderRuleOperandField({
          base: `${base}.${ruleType}`,
          readOnly,
          values,
          setFieldValue,
          operationType,
        })}

      {(operationType === LogicRulesNames.Contains ||
        operationType === notLogicRule(LogicRulesNames.Contains)) && (
        <UploadButton
          base={`${base}.${ruleType}`}
          setFieldValue={setFieldValue}
          operationType={operationType}
        />
      )}
    </>
  );
};
